import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Node, Edge, Connection } from 'reactflow';
import { RAFQueue } from '../utils/RAFQueue';

interface FlowState {
  nodes: Map<string, Node>;
  edges: Map<string, Edge>;
  selectedNodeId: string | null;
  isDragging: boolean;
  error: string | null;

  // Node operations
  addNode: (node: Node) => void;
  updateNode: (id: string, updates: Partial<Node>) => void;
  removeNode: (id: string) => void;
  setNodes: (nodes: Node[]) => void;
  
  // Edge operations
  addEdge: (params: Edge | Connection) => void;
  updateEdge: (id: string, updates: Partial<Edge>) => void;
  removeEdge: (id: string) => void;
  setEdges: (edges: Edge[]) => void;
  
  // Selection
  setSelectedNode: (id: string | null) => void;
  
  // Drag state
  setDragging: (isDragging: boolean) => void;
  
  // Batch operations
  batchUpdateNodes: (updates: Map<string, Partial<Node>>) => void;
  batchUpdateEdges: (updates: Map<string, Partial<Edge>>) => void;
}

// Initialize RAF queue with error handler
const rafQueue = new RAFQueue((error) => {
  console.error('Flow store operation failed:', error);
  useFlowStore.setState({ error: error.message });
});

export const useFlowStore = create<FlowState>()(
  devtools(
    (set, get) => ({
      nodes: new Map<string, Node>(),
      edges: new Map<string, Edge>(),
      selectedNodeId: null,
      isDragging: false,
      error: null,

      addNode: (node: Node) =>
        rafQueue.add(() =>
          set((state) => ({
            nodes: new Map(state.nodes).set(node.id, node),
          }))
        ),

      updateNode: (id: string, updates: Partial<Node>) =>
        rafQueue.add(() =>
          set((state) => {
            const node = state.nodes.get(id);
            if (!node) return state;
            const updatedNodes = new Map(state.nodes);
            updatedNodes.set(id, { ...node, ...updates });
            return { nodes: updatedNodes };
          })
        ),

      removeNode: (id: string) =>
        rafQueue.add(() =>
          set((state) => {
            const nodes = new Map(state.nodes);
            nodes.delete(id);
            return { nodes };
          })
        ),

      setNodes: (nodes: Node[]) =>
        rafQueue.add(() =>
          set({
            nodes: new Map(nodes.map((node) => [node.id, node])),
          })
        ),

      addEdge: (params: Edge | Connection) =>
        rafQueue.add(() =>
          set((state) => {
            const edge: Edge = {
              id: params.id || `${params.source}-${params.target}`,
              source: params.source,
              target: params.target,
              type: (params as Edge).type || 'default',
              animated: true,
              sourceHandle: params.sourceHandle,
              targetHandle: params.targetHandle,
              ...(params as Edge),
            };
            return {
              edges: new Map(state.edges).set(edge.id, edge),
            };
          })
        ),

      updateEdge: (id: string, updates: Partial<Edge>) =>
        rafQueue.add(() =>
          set((state) => {
            const edge = state.edges.get(id);
            if (!edge) return state;
            const updatedEdges = new Map(state.edges);
            updatedEdges.set(id, { ...edge, ...updates });
            return { edges: updatedEdges };
          })
        ),

      removeEdge: (id: string) =>
        rafQueue.add(() =>
          set((state) => {
            const edges = new Map(state.edges);
            edges.delete(id);
            return { edges };
          })
        ),

      setEdges: (edges: Edge[]) =>
        rafQueue.add(() =>
          set({
            edges: new Map(edges.map((edge) => [edge.id, edge])),
          })
        ),

      setSelectedNode: (id: string | null) =>
        rafQueue.add(() =>
          set((state) => {
            const updatedNodes = new Map(state.nodes);
            updatedNodes.forEach((node) => {
              if (node.selected !== (node.id === id)) {
                updatedNodes.set(node.id, {
                  ...node,
                  selected: node.id === id,
                  data: {
                    ...node.data,
                    selected: node.id === id,
                  },
                });
              }
            });
            return {
              selectedNodeId: id,
              nodes: updatedNodes,
            };
          })
        ),

      setDragging: (isDragging: boolean) =>
        rafQueue.add(() =>
          set({
            isDragging,
          })
        ),

      batchUpdateNodes: (updates: Map<string, Partial<Node>>) =>
        rafQueue.add(() =>
          set((state) => {
            const updatedNodes = new Map(state.nodes);
            updates.forEach((updates, id) => {
              const node = updatedNodes.get(id);
              if (node) {
                updatedNodes.set(id, { ...node, ...updates });
              }
            });
            return { nodes: updatedNodes };
          })
        ),

      batchUpdateEdges: (updates: Map<string, Partial<Edge>>) =>
        rafQueue.add(() =>
          set((state) => {
            const updatedEdges = new Map(state.edges);
            updates.forEach((updates, id) => {
              const edge = updatedEdges.get(id);
              if (edge) {
                updatedEdges.set(id, { ...edge, ...updates });
              }
            });
            return { edges: updatedEdges };
          })
        ),
    }),
    { name: 'flow-store' }
  )
);